import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { styled } from '@mui/material'
import { useIntl } from 'react-intl'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import Loading from '../../components/process-state-notifications/Loading'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { usePageState } from '../../hooks/usePageState'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import store from '../../../store'
import { requireSave } from '../../../store/requiredSaveData'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { AggregateField } from '../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../lib/functions/workload'
import { useProgramProjectReportGridOptions } from './gridOptions'
import { statusFilterOptions } from './gridOptions/columnDefs'
import { useProgramProjectReportData } from './hooks/useProgramProjectReportData'
import { useUnitSetting } from './hooks/useUnitSetting'
import {
  PROGRAM_PROJECT_REPORT_ROW_TYPE,
  ProgramProjectReportRow,
} from './model/reportRow'
import { UnitHeader } from './components/UnitHeader'
import { UnitSettingDialog } from './components/UnitSettingDialog'

const ProgramProjectReportPage = ({ uuid }: PageProps) => {
  const intl = useIntl()
  const gridRef = useRef<AgGridReact<ProgramProjectReportRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const [gridApi, setGridApi] = useState<GridApi>()
  const [saving, setSaving] = useState(false)
  const { data, loading, fetch, save, savedAggregateField } =
    useProgramProjectReportData()

  const { workloadUnit, updatePageState } = usePageState(uuid, {
    workloadUnit: WorkloadUnit.HOUR,
  })
  const setWorkloadUnit = useCallback(
    (unit: WorkloadUnit) => updatePageState({ workloadUnit: unit }),
    [updatePageState]
  )

  const unitSetting = useUnitSetting(
    savedAggregateField,
    workloadUnit,
    setWorkloadUnit
  )
  const gridOptions = useProgramProjectReportGridOptions()
  const workloadUnitState = useWorkloadUnit(workloadUnit)
  const gridContext = useMemo(
    () => ({
      programProjectReportTypes: Object.values(PROGRAM_PROJECT_REPORT_ROW_TYPE),
      programProjectReportStatuses: statusFilterOptions,
      workloadUnitState,
      aggregateField: unitSetting.aggregateField,
    }),
    [workloadUnitState, unitSetting.aggregateField]
  )
  useEffect(() => {
    gridApi?.refreshCells({ force: true })
    gridApi?.refreshHeader()
  }, [gridApi, gridContext])
  const [isChangeDialogOpen, setChangeDialogOpen] = useState(false)
  const isInitialDialogOpen = !loading && !unitSetting.isUnitSettled

  const bulkSheetState = useBulkSheetState(uuid, 'ProgramProjectReport')
  const columnState = useColumnState({
    gridApi,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api)
    const columns = event.api.getAllGridColumns() ?? []
    const statusIndex = columns.findIndex(col => col.getColId() === 'status')
    const hasAutoColumn = columns.some(
      col => col.getColId() === 'ag-Grid-AutoColumn'
    )
    if (statusIndex >= 0 && hasAutoColumn) {
      event.api.moveColumns(['ag-Grid-AutoColumn'], statusIndex)
    }
  }, [])

  const onCellValueChanged = useCallback(() => {
    store.dispatch(requireSave())
  }, [])

  const persist = useCallback(
    async (
      aggregateField: AggregateField,
      options?: { clearProgress?: boolean }
    ): Promise<boolean> => {
      setSaving(true)
      try {
        const response = await save(aggregateField, options)
        if (!response.hasError && !response.hasWarning) {
          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.SUCCESS,
              title: intl.formatMessage({ id: 'registration.complete' }),
            })
          )
          await fetch()
          return true
        }
        return false
      } finally {
        setSaving(false)
      }
    },
    [save, fetch, intl]
  )

  const onSubmit = useCallback(async () => {
    gridApi?.stopEditing()
    await persist(unitSetting.aggregateField)
  }, [gridApi, unitSetting.aggregateField, persist])

  const onSettleUnit = useCallback(
    async (field: AggregateField, unit: WorkloadUnit) => {
      unitSetting.selectUnit(field, unit)
      await persist(field)
    },
    [unitSetting, persist]
  )

  const onChangeUnit = useCallback(
    async (
      field: AggregateField,
      unit: WorkloadUnit,
      deleteExistingData: boolean
    ) => {
      unitSetting.selectUnit(field, unit)
      setChangeDialogOpen(false)
      await persist(field, { clearProgress: deleteExistingData })
    },
    [unitSetting, persist]
  )

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [onSubmit]
  )

  return (
    <PageArea>
      <SavePopper loading={saving} onSubmit={onSubmit} />
      <Header>
        <ReloadButton onReload={fetch} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        {unitSetting.isUnitSettled && (
          <UnitHeader
            aggregateField={unitSetting.aggregateField}
            workloadUnit={unitSetting.workloadUnit}
            onChangeWorkloadUnit={unitSetting.setWorkloadUnit}
            onClickChange={() => setChangeDialogOpen(true)}
          />
        )}
      </Header>
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        context={gridContext}
        rowData={data}
        onGridReady={handleGridReady}
        onCellValueChanged={onCellValueChanged}
        onFilterChanged={columnState.onFilterChanged}
        onSortChanged={columnState.onSortChanged}
      />
      <Loading isLoading={loading} elem={bulkSheetRef.current} />

      <UnitSettingDialog
        mode="initial"
        isOpen={isInitialDialogOpen}
        defaultField={unitSetting.aggregateField}
        defaultWorkloadUnit={WorkloadUnit.HOUR}
        onSubmit={onSettleUnit}
      />
      <UnitSettingDialog
        mode="change"
        isOpen={isChangeDialogOpen}
        defaultField={unitSetting.aggregateField}
        defaultWorkloadUnit={unitSetting.workloadUnit}
        onSubmit={onChangeUnit}
        onClose={() => setChangeDialogOpen(false)}
      />
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export const ProgramProjectReport = projectPrivate(ProgramProjectReportPage)
